/**
 * "Fel som återkommer" (pedagogy spec §3.3.5 item 5): the slips that came back in the last 30
 * days, one row per kind with its count, a bar in proportion to the largest, the topics it
 * happened in and a link that opens a short numeric batch on those topics.
 *
 * A kind is shown from two occurrences and `other` never is (the server decides, §3.3.3); a kind
 * this page has no wording for is left out rather than shown as a bare key.
 */
import { Link } from "react-router";
import { Card } from "@client/components/ui";
import { FOCUSED_COUNT, practiceDeepLink } from "@client/features/practice/deepLink";
import { useT, useTp } from "@client/i18n";
import { SLIP_WINDOW_DAYS } from "@shared/evidence";
import type { SlipProgress } from "./types";

/** The kinds `progress.slips.kind.*` has wording for, in the order the server lists them. */
const KNOWN_KINDS: readonly string[] = [
  "power-of-ten",
  "unit",
  "sign",
  "factor",
  "classical-for-relativistic",
  "wrong-principle",
  "plan-miss",
];

export interface SlipsCardProps {
  slips: SlipProgress[];
  /** A topic's name in the active language. */
  topicLabel: (topicId: string) => string;
}

export function SlipsCard({ slips, topicLabel }: SlipsCardProps) {
  const t = useT();
  const tp = useTp();
  const shown = slips.filter((slip) => KNOWN_KINDS.includes(slip.kind));
  const largest = Math.max(1, ...shown.map((slip) => slip.count));
  const title = t("progress.slips.title");

  return (
    <Card title={title} description={tp("progress.slips.window", SLIP_WINDOW_DAYS)} compact>
      {shown.length === 0 ? (
        <p className="text-sm text-ink-600 dark:text-ink-400">
          {tp("progress.slips.empty", SLIP_WINDOW_DAYS)}
        </p>
      ) : (
        <ul aria-label={title} className="divide-y divide-ink-100 dark:divide-ink-900">
          {shown.map((slip) => {
            const kind = t(`progress.slips.kind.${slip.kind}`);
            const practise = tp("progress.slips.practise", FOCUSED_COUNT);
            return (
              <li key={slip.kind} className="py-3 first:pt-0 last:pb-0">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm font-medium text-ink-800 dark:text-ink-100">{kind}</span>
                  <span className="shrink-0 text-sm tabular-nums text-ink-700 dark:text-ink-200">
                    {tp("progress.slips.count", slip.count)}
                  </span>
                </div>
                {/* The count above is the information; the bar only shows it at a glance. */}
                <div
                  aria-hidden="true"
                  className="mt-1.5 h-1.5 w-full rounded-full bg-ink-200 dark:bg-ink-800"
                >
                  <div
                    data-testid="slip-bar"
                    className="h-full rounded-full bg-accent-600 dark:bg-accent-400"
                    style={{ width: `${Math.round((slip.count / largest) * 100)}%` }}
                  />
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                  <span className="text-xs text-ink-600 dark:text-ink-400">
                    {slip.topics.map(topicLabel).join(" · ")}
                  </span>
                  <Link
                    to={practiceDeepLink({
                      topics: slip.topics,
                      types: ["numeric"],
                      count: FOCUSED_COUNT,
                      primary: true,
                    })}
                    aria-label={`${practise}: ${kind}`}
                    className="text-sm font-medium text-accent-700 underline hover:text-accent-800 dark:text-accent-300 dark:hover:text-accent-200"
                  >
                    {practise}
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

export default SlipsCard;
